// Sorte de feuille « limites » au format publié (plugin `limit` du noyau, lot I1) — mécanisme général ; les gestes
// et leurs libellés viennent du pack `analyse.limites`.
//
// Une ligne est l'une de :
//   - « E → ℓ » : la limite de E au point (kernel.point) vaut ℓ (nombre, +∞, −∞, 0⁺, 0⁻) ;
//   - « A = B » (chaîne permise) : réécriture de la même fonction ; « A = P + o(g) » : développement limité, vrai
//     si (A − P) ÷ g → 0 ;
//   - « A ∼ B » : équivalents au point, vrai si A ÷ B → 1 ;
//   - un encadrement « g ≤ E ≤ h » (vrai près du point), pour le théorème des gendarmes.
// La vérité vient du calcul des limites du noyau (`limitValue` : symbolique, sinon numérique), jamais d'un corrigé.

import { parse } from './syntax.js';
import { isLeaf, symbols } from './ast.js';
import { light, keyOf } from './rewrite.js';
import { evaluate, isRelation, toF } from './equiv.js';
import { limitValue, limitSym, pointOf } from './calculus.js';

/** Découpe une ligne à « → » ou « ∼ » (hors parenthèses) : { op, left, right } ou null. */
export function splitLimitLine(text) {
  let depth = 0;
  const s = String(text);
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '(' || c === '[') depth++;
    else if (c === ')' || c === ']') depth--;
    else if (depth === 0 && (c === '→' || c === '∼' || c === '~')) {
      return {
        op: c === '→' ? 'to' : 'equiv',
        left: s.slice(0, i).trim(),
        right: s.slice(i + 1).trim(),
      };
    }
  }
  return null;
}

// « o(g) » : un reste négligeable devant g au point. Remplacé par un symbole pour le lecteur, puis retiré.
const O_RE = /\bo\(/gu;
export function extractSmallO(text) {
  const out = [];
  let s = String(text),
    i;
  while ((i = s.search(O_RE)) >= 0) {
    let depth = 0,
      j = i + 1;
    for (; j < s.length; j++) {
      if (s[j] === '(') depth++;
      else if (s[j] === ')') {
        depth--;
        if (depth === 0) break;
      }
    }
    out.push(s.slice(i + 2, j));
    s = s.slice(0, i) + '0' + s.slice(j + 1);
  }
  return { text: s, rests: out };
}

const num = (v) =>
  typeof v === 'number' ? v : v && v.q !== undefined ? Number(v.p) / Number(v.q) : NaN;
export const limNum = num;
export function sameLimit(a, b) {
  const x = num(a),
    y = num(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return x === y;
  return Math.abs(x - y) <= 1e-3 * Math.max(1, Math.abs(x), Math.abs(y));
}

/**
 * Limite au point : symbolique (opérations, croissances comparées), sinon numérique sur une suite de points qui
 * approche le point (h = 10⁻¹ … 10⁻⁴ ou 10² … 10⁶), avec extrapolation — assez stable pour les formes
 * indéterminées des développements limités. NaN si elle ne se stabilise pas.
 */
export function trueLimit(E, P, table) {
  try {
    const r = limitSym(E, P.v, P.point, table, {}, { cc: true, side: P.side });
    if (r && !r.fi && !r.none && 'v' in r) return num(r.v);
  } catch {
    /* repli numérique */
  }
  return numericLimit(E, P, table);
}

export function numericLimit(E, P, table) {
  const p = P.point;
  const xs = [];
  for (let k = 1; k <= 12; k++) {
    if (p === Infinity) xs.push(10 ** k);
    else if (p === -Infinity) xs.push(-(10 ** k));
    else xs.push(p + (P.side < 0 ? -1 : 1) * 10 ** -k);
  }
  const vs = [];
  for (const x of xs) {
    let v;
    try {
      v = num(evaluate(E, { [P.v]: x }, table));
    } catch {
      v = NaN;
    }
    if (!Number.isFinite(v)) {
      // dépassement des flottants après une croissance de même signe : limite infinie
      if (
        (v === Infinity || v === -Infinity) &&
        vs.length >= 2 &&
        Math.abs(vs.at(-1)) > Math.abs(vs.at(-2)) &&
        Math.sign(vs.at(-1)) === Math.sign(v)
      )
        return v;
      break;
    }
    vs.push(v);
  }
  if (vs.length < 3) return NaN;
  // convergence : on suit la suite tant que les écarts entre points diminuent (au-delà, l'erreur d'arrondi grandit)
  let k = 1;
  while (k + 1 < vs.length && Math.abs(vs[k + 1] - vs[k]) <= Math.abs(vs[k] - vs[k - 1])) k++;
  const v = vs[k],
    d = Math.abs(vs[k] - vs[k - 1]);
  if (k >= 2 && d <= 1e-3 * Math.max(1, Math.abs(v))) return v;
  // croissance sans borne : la valeur grandit régulièrement, toujours du même signe, et dépasse 10⁶
  const grows = vs.every(
    (x, i) =>
      i === 0 || (Math.abs(x) > Math.abs(vs[i - 1]) && Math.sign(x) === Math.sign(vs[i - 1])),
  );
  if (grows && Math.abs(vs.at(-1)) > 1e6) return Math.sign(vs.at(-1)) * Infinity;
  return NaN;
}

/** Ce que concluent les opérations seules (sans croissances comparées), ou null si une forme indéterminée reste. */
export function opsLimit(E, P, table, cc = false) {
  try {
    const r = limitSym(E, P.v, P.point, table, {}, { cc, side: P.side });
    return r && !r.fi && !r.none && 'v' in r ? num(r.v) : null;
  } catch {
    return null;
  }
}

/** Comme opsLimit, avec des morceaux remplacés par des symboles de limite connue (env). */
export function opsLimitEnv(E, P, table, env) {
  try {
    const r = limitSym(E, P.v, P.point, table, env, { cc: true, side: P.side });
    return r && !r.fi && !r.none && 'v' in r ? num(r.v) : null;
  } catch {
    return null;
  }
}

/** Point de la feuille : { ast, point, side, v } */
export function limitPoint(kernel, table) {
  const ast = parse(String(kernel.point ?? '+∞'), table);
  const { point, side } = pointOf(ast, table);
  return { ast, point: num(point), side, v: kernel.goal?.target ?? 'x' };
}

/** Une relation (égalité, encadrement) est-elle vraie près du point ? (quelques valeurs proches) */
export function holdsNear(rel, P, table) {
  const pts = [];
  const p = P.point;
  if (p === Infinity) pts.push(37, 101, 997, 10007);
  else if (p === -Infinity) pts.push(-37, -101, -997, -10007);
  else
    for (const h of [0.1, 0.01, 0.001]) {
      if (P.side >= 0) pts.push(p + h);
      if (P.side <= 0) pts.push(p - h);
    }
  let seen = 0;
  for (const x of pts) {
    const r = evaluate(rel, { [P.v]: x }, table);
    if (r === false) return false;
    if (r === true) seen++;
  }
  return seen > 0;
}

/** Un « reste » (lettre, opérations) sous la forme la plus simple : nombre d'opérations. */
export const opsOf = (n) => (isLeaf(n) ? 0 : 1 + n.k.reduce((s, c) => s + opsOf(c), 0));
export const occurrences = (n, v) =>
  n.t === 'Sym'
    ? n.name === v
      ? 1
      : 0
    : isLeaf(n)
      ? 0
      : n.k.reduce((s, c) => s + occurrences(c, v), 0);
export { light, keyOf, isRelation, toF, symbols };
